"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { Megaphone, Plus } from "lucide-react";

import { getRecentUserBroadcasts } from "@/server-actions/broadcasts/actions";

function formatBroadcastMessage(message) {
  if (!message) return { text: "", resourceLink: null };
  const resourceLinkMatch = message.match(/Resource Link:\s+(https?:\/\/[^\s]+)/);
  if (!resourceLinkMatch) return { text: message, resourceLink: null };
  const [fullMatch, url] = resourceLinkMatch;
  // Only strip the marker when the URL is actually a /resources/ path —
  // otherwise leave external/malformed links intact in the announcement text.
  const resourcePathMatch = url.match(/^https?:\/\/[^/]+(\/resources\/[^?#\s]+)/);
  if (!resourcePathMatch) return { text: message, resourceLink: null };
  const text = message.replace(fullMatch, "").trim();
  return { text, resourceLink: resourcePathMatch[1] };
}

function formatDate(value) {
  try {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return new Intl.DateTimeFormat(undefined, {
      month: "short",
      day: "numeric",
    }).format(date);
  } catch (error) {
    return "";
  }
}

/**
 * Slim, single-line studio announcement pill for the calm home dashboard.
 * Self-fetches the latest broadcast (mirrors BroadcastSection's data source).
 */
export default function BroadcastSlim({ canBroadcast = false }) {
  const [broadcast, setBroadcast] = useState(null);

  useEffect(() => {
    let active = true;
    getRecentUserBroadcasts()
      .then((data) => {
        if (active && Array.isArray(data) && data.length) setBroadcast(data[0]);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  if (!broadcast) return null;

  const { text, resourceLink } = formatBroadcastMessage(broadcast.message);
  const sender =
    broadcast.sender?.name || broadcast.sender?.email || "Studio Admin";
  const date = formatDate(broadcast.sentAt || broadcast.createdAt);

  return (
    <div className="mb-6 flex items-center gap-2.5 rounded-full border border-brand-100 bg-brand-50 px-3.5 py-2 text-[13px]">
      <Megaphone className="h-3.5 w-3.5 shrink-0 text-brand-600" />
      <span className="min-w-0 flex-1 truncate text-gray-600">
        <span className="font-semibold text-gray-900">{sender}</span>{" "}
        {broadcast.subject ? `${broadcast.subject} — ` : ""}
        {text}
      </span>
      {resourceLink && (
        <Link
          href={resourceLink}
          className="shrink-0 font-semibold text-brand-700 hover:text-brand-800"
        >
          View →
        </Link>
      )}
      {date && <span className="shrink-0 text-xs text-gray-400">{date}</span>}
      {canBroadcast && (
        <Link
          href="/workspaces/broadcast"
          title="Send a broadcast"
          aria-label="Send a broadcast"
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-brand-200 bg-white text-brand-700 hover:bg-brand-100"
        >
          <Plus className="h-3.5 w-3.5" />
        </Link>
      )}
    </div>
  );
}
